import { IPermissionProps } from '@/types/shared'
import React from 'react'
import { JobAttribute } from '../../types'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { createJob, getJobCategories } from '../../services'
import { useNavigate } from 'react-router-dom'
import SubHeaders from '@/components/Headers/SubHeaders'
import {
    generateSlug,
    JOB_COUNTRY,
    JOB_LEVEL,
    JOB_STATUS,
    JOB_TYPE,
    LOCALE_OPTIONS,
} from '../../constants'
import { paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { createJobSchema } from './yup'
import { getAllDepartments } from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { DatacentralHookForm } from '@/components/ui/form-field'
import type { DatacentralHookFormDataItem } from '@/components/ui/form-field'
import PeopleIcons from '@/icons/PeopleIcons'
import Paper from '@/components/Paper'

/** Shorthand helper — colSpan:1 by default, fullWidth adds responsive col-span-2 */
const f = (
    name: keyof JobAttribute,
    type: 'textfield' | 'textarea' | 'select',
    label: string,
    opts?: {
        placeholder?: string
        required?: boolean
        disabled?: boolean
        options?: { label: string; value: any }[]
        multiple?: boolean
        fullWidth?: boolean
    }
): DatacentralHookFormDataItem<JobAttribute> => ({
    colSpan: 1,
    className: opts?.fullWidth ? 'md:col-span-2' : undefined,
    formItemProps: {
        name,
        type,
        label,
        placeholder: opts?.placeholder ?? label,
        required: opts?.required,
        disabled: opts?.disabled,
        ...(type === 'select'
            ? { options: opts?.options ?? [], multiple: opts?.multiple }
            : {}),
    } as any,
})

const Create: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const { mutateAsync: create, isPending } = useMutationWithHandler(
        { mutationFn: createJob },
        {
            reactQueryKeys: QUERY_KEYS.JOBS,
            successMessage: t('created-success'),
            onSuccess: () => navigate('/jobs'),
        }
    )

    const { data: categories } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.JOB_CATEGORIES_LIST],
        queryFn: getJobCategories,
    })
    const { data: departments } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DEPARTMENTS],
        queryFn: getAllDepartments,
    })

    const formControl = useForm<JobAttribute>({
        defaultValues: {
            title: '',
            description: '',
            slug: '',
            locale: 'vi-VN',
            categories: [],
            location: '',
            level: '',
            type: JOB_TYPE[0].value || '',
            summary: '',
            country: JOB_COUNTRY[0].value || '',
            salary: '',
            status: '',
            responsible: '',
            qualification: '',
            benefit: '',
            department: '',
            required_skills: '',
        },
        //@ts-ignore
        resolver: yupResolver(createJobSchema),
    })

    const title = formControl.watch('title')
    React.useEffect(() => {
        formControl.setValue('slug', generateSlug(title || ''))
    }, [title, formControl])

    const catOpts =
        categories?.results.map((c) => ({ label: c.title, value: c.id })) || []
    const deptOpts =
        departments?.results.map((d) => ({
            label: d.department_name,
            value: d.department_name,
        })) || []

    const formData: DatacentralHookFormDataItem<JobAttribute>[] = [
        // Row 1
        f('title', 'textfield', t('job-title'), {
            placeholder: t('input-job-title'),
            required: true,
        }),
        f('description', 'textfield', t('job-description')),
        // Row 2
        f('slug', 'textfield', 'Slug', { disabled: true }),
        f('locale', 'select', t('job-locale'), {
            placeholder: t('select-job-locale'),
            options: LOCALE_OPTIONS,
            required: true,
        }),
        // Row 3
        f('categories', 'select', t('job-categories'), {
            options: catOpts,
            multiple: true,
            required: true,
        }),
        f('department', 'select', t('department'), {
            options: deptOpts,
            required: true,
        }),
        // Row 4
        f('level', 'select', t('job-level'), {
            placeholder: t('input-job-level'),
            options: JOB_LEVEL,
            required: true,
        }),
        f('type', 'select', t('job-type'), {
            placeholder: t('input-job-type'),
            options: JOB_TYPE,
            required: true,
        }),
        // Row 5
        f('country', 'select', t('job-country'), {
            placeholder: t('input-job-country'),
            options: JOB_COUNTRY,
            required: true,
        }),
        f('status', 'select', t('job-status'), {
            placeholder: t('select-job-status'),
            options: JOB_STATUS(t),
            required: true,
        }),
        // Row 6
        f('salary', 'textarea', t('job-salary'), {
            placeholder: t('input-job-salary'),
            required: true,
        }),
        f('location', 'textarea', t('job-location'), {
            placeholder: t('input-job-location'),
            required: true,
        }),
        // Row 7
        f('summary', 'textarea', t('job-summary'), {
            placeholder: t('input-job-summary'),
            fullWidth: true,
        }),
        // Full-width rows
        f('responsible', 'textarea', t('job-responsible'), {
            placeholder: t('input-job-responsible'),
            required: true,
            fullWidth: true,
        }),
        f('qualification', 'textarea', t('job-qualification'), {
            placeholder: t('input-job-qualification'),
            required: true,
            fullWidth: true,
        }),
        f('benefit', 'textarea', t('job-benefit'), {
            placeholder: t('input-job-benefit'),
            required: true,
            fullWidth: true,
        }),
        f('required_skills', 'textarea', t('job-required-skills'), {
            placeholder: t('input-job-required-skills'),
            fullWidth: true,
        }),
    ]

    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    { path: '/jobs', title: t('job-list') },
                    { path: paths.jobCreate, title: t('job-create') },
                ]}
            />
            <Paper className="mt-2">
                <DatacentralHookForm
                    formControl={
                        formControl as unknown as UseFormReturn<JobAttribute>
                    }
                    columns={2}
                    className="!grid-cols-1 md:!grid-cols-2"
                    data={formData}
                    head={{
                        title: t('job-information'),
                        icon: <PeopleIcons />,
                        loadingAction: isPending,
                    }}
                    actions={[
                        {
                            title: t('create-job'),
                            type: 'submit',
                            variant: 'default',
                            loading: isPending,
                            disabled: isPending,
                        },
                    ]}
                    onSubmit={async (values) => {
                        try {
                            await create(values)
                        } catch (e) {
                            console.error('Error submitting form', e)
                        }
                    }}
                />
            </Paper>
        </>
    )
}

export default Create
